import { useState, useEffect } from 'react';
import { getAnalyticsSummary, getAnalyticsOrders, getAnalyticsAreas, getAnalyticsServices } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const COLORS = ['#6C63FF', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#E7E9ED', '#00BFA5', '#FF5722'];

export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [areas, setAreas] = useState([]);
  const [services, setServices] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [areaFilter, setAreaFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadOrders(); }, [period, areaFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sum, ord, ar, svc] = await Promise.all([
        getAnalyticsSummary(areaFilter),
        getAnalyticsOrders(period, areaFilter),
        getAnalyticsAreas(),
        getAnalyticsServices(areaFilter),
      ]);
      setSummary(sum);
      setOrders(ord);
      setAreas(ar);
      setServices(svc);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadOrders = async () => {
    try {
      const [sum, ord, svc] = await Promise.all([
        getAnalyticsSummary(areaFilter),
        getAnalyticsOrders(period, areaFilter),
        getAnalyticsServices(areaFilter),
      ]);
      setSummary(sum);
      setOrders(ord);
      setServices(svc);
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="page"><p>Loading analytics...</p></div>;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>📊 Analytics</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd' }}>
            <option value="">All Areas</option>
            {(summary?.available_areas || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd' }}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          <SummaryCard title="Today" orders={summary.today.orders} revenue={summary.today.revenue} completed={summary.today.completed} color="#6C63FF" />
          <SummaryCard title="This Week" orders={summary.week.orders} revenue={summary.week.revenue} completed={summary.week.completed} color="#00BFA5" />
          <SummaryCard title="This Month" orders={summary.month.orders} revenue={summary.month.revenue} completed={summary.month.completed} color="#FF6384" />
          <SummaryCard title="All Time" orders={summary.total.orders} revenue={summary.total.revenue} completed={summary.total.completed} color="#FF9F40" />
        </div>
      )}

      {/* Orders Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>📈 Orders ({period})</h3>
          {orders.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={orders}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="orders" fill="#6C63FF" name="Total Orders" />
                <Bar dataKey="completed" fill="#4CAF50" name="Completed" />
                <Bar dataKey="cancelled" fill="#FF5722" name="Cancelled" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#999', textAlign: 'center' }}>No data yet</p>}
        </div>

        {/* Area Pie Chart */}
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>📍 Orders by Area</h3>
          {areas.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={areas.slice(0, 6)} dataKey="orders" nameKey="area" cx="50%" cy="50%" outerRadius={80} label>
                  {areas.slice(0, 6).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#999', textAlign: 'center' }}>No data yet</p>}
        </div>
      </div>

      {/* Revenue Chart + Popular Services */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>💰 Revenue ({period})</h3>
          {orders.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={orders}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip formatter={(v) => `₹${v}`} />
                <Line type="monotone" dataKey="revenue" stroke="#00BFA5" strokeWidth={2} dot={{ r: 4 }} name="Revenue (₹)" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p style={{ color: '#999', textAlign: 'center' }}>No data yet</p>}
        </div>

        {/* Popular Services */}
        <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>🔥 Popular Services</h3>
          {services.length > 0 ? (
            <div>
              {services.map((s, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: 13 }}>{i + 1}. {s.service}</span>
                  <span style={{ fontSize: 12, color: '#666' }}>{s.orders} orders</span>
                </div>
              ))}
            </div>
          ) : <p style={{ color: '#999', textAlign: 'center' }}>No data yet</p>}
        </div>
      </div>

      {/* Area Table */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>📍 Area-wise Breakdown</h3>
        <table className="data-table">
          <thead>
            <tr><th>Area</th><th>Total Orders</th><th>Completed</th><th>Revenue (₹)</th></tr>
          </thead>
          <tbody>
            {areas.map((a, i) => (
              <tr key={i}>
                <td><strong>{a.area}</strong></td>
                <td>{a.orders}</td>
                <td>{a.completed}</td>
                <td>₹{a.revenue.toFixed(0)}</td>
              </tr>
            ))}
            {areas.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>No booking data yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ title, orders, revenue, completed, color }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` }}>
      <p style={{ margin: 0, fontSize: 12, color: '#888' }}>{title}</p>
      <h2 style={{ margin: '4px 0', fontSize: 22, color }}>{orders}</h2>
      <p style={{ margin: 0, fontSize: 11, color: '#666' }}>orders • {completed} completed</p>
      <p style={{ margin: '2px 0 0', fontSize: 12, fontWeight: 600, color: '#00BFA5' }}>₹{revenue.toFixed(0)} revenue</p>
    </div>
  );
}
